import { Button, Typography } from "@material-ui/core"
import { extractData, getShowroomAll, ShowRoom } from "api"
import { trackPopUpOpen } from "common/analytics"
import NextLink from "common/bit/NextLink"
import { Desktop, useIsDesktop } from "common/MediaQueries"
import useGeoLocation from "hooks/useGeolocation"
import { useEffect, useState } from "react"
import CalendarIcon from "reusable/icons/CalendarIcon"
import InnerPageWrapper from "reusable/wrappers/InnerPageWrapper"
import styled from "styled-components"
import colors from "theme/colors"
import ErrorLocationPopUp from "../../../reusable/popups/ErrorLocationPopUp"
import TwoToggleButtons from "../../../reusable/TwoToggleButtons"
import AreSelectionButtons from "../AreSelectionButtons"
import DynamicMap from "../map/DynamicMap"
import { useMapContext } from "../map/MapContext"
import {
  Areas,
  locationFilterListShowRooms,
  ToggleButtons,
} from "../map/utils/mapFilters"
import ShowRoomList from "./ShowRoomList"

const StyledInnerPageWrapper = styled(InnerPageWrapper)`
  [class*="StyledSubtitle"] {
    max-width: 540px;
    margin: 20px auto 0;
  }
`

const StyledContentWrap = styled.div`
  display: flex;
  padding-top: 20px;
  width: 100%;
`
const StyledMeetingButton = styled(Button)`
  background-color: ${(p) => p.theme.colors.white};
  /* .MuiButton-label {
    gap: 10px;
    display: flex;
    align-items: center;
  } */
  &:hover,
  &:focus-visible {
    path {
      stroke: ${colors.white};
    }
  }
`
const StyledToggleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: space-between;
  background-color: ${(p) => p.theme.colors.white};
  padding: 16px;
  border-radius: 4px;
  width: 100%;
  margin-top: 40px;
  gap: 20px;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
  }
`
const StyledTwoToggleButtons = styled(TwoToggleButtons)`
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 320px;
    margin: 0;
  }
`
const StyledAreSelectionButtons = styled(AreSelectionButtons)`
  margin-top: 20px;
`
const StyledContentSection = styled.section`
  width: 100%;
  direction: ltr;
  > * {
    direction: rtl;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 50%;
    flex-shrink: 0;
    height: 680px;
    overflow-y: auto;
  }
`
const StyledMapSection = styled.section`
  flex-grow: 1;
  flex-shrink: 0;
  width: 50%;
  height: 680px;
  position: sticky;
  /* top: var(--sticky-height); */
  transition: top 221ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  align-self: flex-start;
  padding: 0px 25px;
  padding-left: 0;
`

const ShowRoomsPage = () => {
  const {
    setStores,
    stores,
    setMap,
    setMaps,
    setFilterStores,
    setSelectedStore,
  } = useMapContext<ShowRoom>()
  // todo change to local storage:
  const [userHasSeenPopUpError, setUserHasSeenPopUpError] = useState(false)
  const [openErrorPopUp, setOpenErrorPopUp] = useState(false)

  // toggle states:
  const [toggleIndex, setToggleIndex] = useState(ToggleButtons.CLOSE_TO_ME)
  const [selectedAreaId, setSelectedAreaId] = useState(Areas.ALL)
  // location states:
  const { loaded, error, lng, lat } = useGeoLocation()
  const isDesktop = useIsDesktop()
  // if user allow location and we have coordinates (lat,lng) call the api
  useEffect(() => {
    let isComponentMounted = true
    if (error.code != "" && !userHasSeenPopUpError) {
      setOpenErrorPopUp(true)
    }
    const fetchData = async () => {
      if (isComponentMounted) {
        if (loaded && lng && lat) {
          const data = await getShowroomAll(lng, lat).then(extractData)
          setStores(data?.body)
        }
      }
    }
    fetchData().catch(console.error)
    return () => {
      isComponentMounted = false
    }
  }, [error, loaded, lng, lat])

  useEffect(() => {
    //Filter options updated so apply all filters here
    let result = stores
    result = locationFilterListShowRooms(result, selectedAreaId, toggleIndex)
    setFilterStores(result)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, selectedAreaId, toggleIndex])

  return (
    <>
      <StyledInnerPageWrapper
        title="אולמות התצוגה של מאזדה"
        subtitle="מוזמנים להתארח באולמות התצוגה שלנו, להתרשם מהדגמים מקרוב וליהנות מהחוויה המלאה של מאזדה"
        imgUrl={
          isDesktop
            ? "/img/cover/Showrooms.jpg"
            : "/img/cover/Showroomsmobile.jpg"
        }
        breadCrumbName="ShowRooms"
      >
        <NextLink href={`/meeting/`} passHref>
          <StyledMeetingButton
            variant="outlined"
            onClick={() => {
              trackPopUpOpen("showrooms meeting")
            }}
            startIcon={
              <CalendarIcon width={24} height={24} color={colors.black} />
            }
          >
            {/* <StyledSvgIcon
              src={`/icons/fi_calendar.svg`}
              alt=""
              w={24}
              h={24}
              role="presentation"
              aria-hidden="true"
            /> */}
            לבחירת דגם ותיאום פגישה
          </StyledMeetingButton>
        </NextLink>
        <StyledToggleWrapper>
          <Typography variant="body1" component="h2">
            חפשו אולמות תצוגה לפי המיקום שלכם, וקבלו מידע על שעות פעילות, דרכי
            הגעה ועוד
          </Typography>
          <StyledTwoToggleButtons
            activeButton={toggleIndex}
            setActiveButton={setToggleIndex}
            optionsList={["קרוב אליי", "לפי אזור"]}
            toggleId="map-toggle"
          />
        </StyledToggleWrapper>
        {toggleIndex === ToggleButtons.AREAS && (
          <StyledAreSelectionButtons
            activeButton={selectedAreaId}
            setActiveButton={setSelectedAreaId}
            onSelectFunction={() => setSelectedStore(null)}
          />
        )}

        <StyledContentWrap>
          <StyledContentSection>
            <ShowRoomList />
          </StyledContentSection>
          <Desktop>
            <StyledMapSection>
              <DynamicMap setMaps={setMaps} setMap={setMap} />
            </StyledMapSection>
          </Desktop>
        </StyledContentWrap>

        <ErrorLocationPopUp
          open={openErrorPopUp}
          setOpen={setOpenErrorPopUp}
          setUserHasSeenPopUpError={setUserHasSeenPopUpError}
        />
      </StyledInnerPageWrapper>
    </>
  )
}

export default ShowRoomsPage
